<!-- Page Header -->
<div class="d-flex align-items-center justify-content-between mb-4">
  <div>
    <h4 class="erp-page-title mb-0">User Management</h4>
    <p class="text-muted fs-7 mb-0">Manage team members, roles and access</p>
  </div>
  <button class="btn btn-primary btn-sm" (click)="openCreateModal(userModal)">
    <i class="bi bi-person-plus me-1"></i> Add User
  </button>
</div>

<!-- Users Table -->
<div class="app-card">
  <div class="app-table-container">
    @if (loading()) {
      <div class="erp-loading-overlay">
        <div class="spinner-border text-primary"></div>
        <p>Loading users&hellip;</p>
      </div>
    } @else if (users().length === 0) {
      <div class="app-table-empty">
        <i class="bi bi-people"></i>
        <p>No users found. <a href="#" (click)="openCreateModal(userModal); $event.preventDefault()">Add the first one.</a></p>
      </div>
    } @else {
      <table class="table app-table app-table-sticky mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
            <th>Status</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (user of users(); track user.id; let i = $index) {
            <tr [class.row-inactive]="!user.is_active">
              <td class="text-muted fs-8">{{ i + 1 }}</td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="um-avatar">{{ (user.firstname?.[0] ?? '') + (user.lastname?.[0] ?? '') | uppercase }}</div>
                  <div class="fw-500">{{ fullName(user) }}</div>
                </div>
              </td>
              <td class="text-muted fs-7">{{ user.email }}</td>
              <td>
                @for (role of user.roles; track role.id) {
                  <span class="erp-badge erp-badge-primary me-1">{{ role.name }}</span>
                }
                @if (!user.roles?.length) { <span class="text-muted">—</span> }
              </td>

                <!-- Status toggle -->
                <td class="text-center">
                  <div class="form-check form-switch d-inline-flex">
                    <input
                      class="form-check-input um-toggle"
                      type="checkbox"
                      role="switch"
                      [checked]="user.is_active"
                      (change)="
                        onToggleStatus(user, $any($event.target).checked)
                      "
                      [id]="'status-' + user.id"
                    />
                  </div>
                </td>

              <!-- Actions -->
              <td>
                <div class="td-actions">
                  <span class="action-btn edit" title="Edit" (click)="openEditModal(user, userModal)"><i class="bi bi-pencil"></i></span>
                  <span class="action-btn" title="Reset password" style="background:var(--erp-status-pending-bg);color:var(--erp-status-pending);" (click)="openResetModal(user, resetModal)"><i class="bi bi-key"></i></span>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
</div>

<!-- ════════════════════════════════════════════════════════════
     MODAL: Create / Edit User
════════════════════════════════════════════════════════════ -->
<ng-template #userModal let-modal>
  <div class="modal-header border-0 pb-0">
    <h5 class="modal-title fw-bold">
      <i class="bi bi-person-{{ isEditMode ? 'gear' : 'plus' }} me-2"></i>
      {{ isEditMode ? 'Edit User' : 'New User' }}
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()">X</button>
  </div>

  <div class="modal-body" [formGroup]="userForm">
    <!-- Avatar preview -->
    <div class="d-flex justify-content-center mb-4">
      <label class="um-avatar-upload" title="Upload photo">
        @if (imagePreview) {
          <img [src]="imagePreview" class="um-avatar-img" alt="preview" />
        } @else {
          <i class="bi bi-camera fs-3 text-muted"></i>
        }
        <input
          type="file"
          accept="image/*"
          hidden
          (change)="onImageChange($event)"
        />
      </label>
    </div>

    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label small fw-semibold text-muted"
          >First Name *</label
        >
        <input
          formControlName="firstname"
          type="text"
          class="form-control"
          [class.is-invalid]="isInvalid('firstname', userForm)"
          placeholder="First name"
        />
        <div class="invalid-feedback">Required.</div>
      </div>
      <div class="col-md-6">
        <label class="form-label small fw-semibold text-muted"
          >Last Name *</label
        >
        <input
          formControlName="lastname"
          type="text"
          class="form-control"
          [class.is-invalid]="isInvalid('lastname', userForm)"
          placeholder="Last name"
        />
        <div class="invalid-feedback">Required.</div>
      </div>
      <div class="col-12">
        <label class="form-label small fw-semibold text-muted">Email *</label>
        <input
          formControlName="email"
          type="email"
          class="form-control"
          [class.is-invalid]="isInvalid('email', userForm)"
          placeholder="email@example.com"
        />
        <div class="invalid-feedback">Valid email required.</div>
      </div>
      <div class="col-12">
        <label class="form-label small fw-semibold text-muted">
          Password {{ isEditMode ? '(leave blank to keep current)' : '*' }}
        </label>
        <input
          formControlName="password"
          type="password"
          class="form-control"
          [class.is-invalid]="isInvalid('password', userForm)"
          placeholder="{{
            isEditMode ? 'New password (optional)' : 'Min 6 characters'
          }}"
        />
        <div class="invalid-feedback">Min 6 characters required.</div>
      </div>
      <div class="col-12">
        <label class="form-label small fw-semibold text-muted">Roles *</label>
        <ng-select
          formControlName="role_ids"
          [items]="roles()"
          bindLabel="name"
          bindValue="id"
          [multiple]="true"
          [closeOnSelect]="false"
          placeholder="Select roles"
          [class.is-invalid]="isInvalid('role_ids', userForm)"
        >
        </ng-select>
        <div class="invalid-feedback">At least one role required.</div>
      </div>
    </div>
  </div>

  <div class="modal-footer border-0 pt-0">
    <button
      type="button"
      class="btn btn-light btn-sm"
      (click)="modal.dismiss()"
    >
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-primary btn-sm"
      [disabled]="saving()"
      (click)="onSaveUser(modal)"
    >
      @if (saving()) {
        <span class="spinner-border spinner-border-sm me-1"></span>
      }
      {{ isEditMode ? 'Save Changes' : 'Create User' }}
    </button>
  </div>
</ng-template>

<!-- ════════════════════════════════════════════════════════════
     MODAL: Reset Password (Admin)
════════════════════════════════════════════════════════════ -->
<ng-template #resetModal let-modal>
  <div class="modal-header border-0 pb-0">
    <h5 class="modal-title fw-bold">
      <i class="bi bi-key me-2 text-warning"></i>
      Reset Password — {{ fullName(selectedUser!) }}
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()">X</button>
  </div>

  <div class="modal-body" [formGroup]="resetPasswordForm">
    <p class="text-muted small mb-3">
      Set a new password for this user. They will need to use it on next login.
    </p>

    <div class="mb-3">
      <label class="form-label small fw-semibold text-muted"
        >New Password *</label
      >
      <input
        formControlName="new_password"
        type="password"
        class="form-control"
        [class.is-invalid]="isInvalid('new_password', resetPasswordForm)"
        placeholder="Min 6 characters"
      />
      <div class="invalid-feedback">Min 6 characters required.</div>
    </div>

    <div class="mb-1">
      <label class="form-label small fw-semibold text-muted"
        >Confirm Password *</label
      >
      <input
        formControlName="new_password_confirmation"
        type="password"
        class="form-control"
        [class.is-invalid]="
          isInvalid('new_password_confirmation', resetPasswordForm)
        "
        placeholder="Repeat new password"
      />
      <div class="invalid-feedback">Required.</div>
    </div>

    @if (resetPasswordForm.errors?.['mismatch'] && resetPasswordForm.touched) {
      <div class="text-danger small mt-1">Passwords do not match.</div>
    }
  </div>

  <div class="modal-footer border-0 pt-0">
    <button
      type="button"
      class="btn btn-light btn-sm"
      (click)="modal.dismiss()"
    >
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-warning btn-sm"
      [disabled]="saving()"
      (click)="onResetPassword(modal)"
    >
      @if (saving()) {
        <span class="spinner-border spinner-border-sm me-1"></span>
      }
      Reset Password
    </button>
  </div>
</ng-template>
